<template lang="pug">
//- What is behind the running clock. A support call produces a subject, a
//- description, dates and screenshots while it happens — and until now the only
//- thing that could be written down without leaving the call was a note. A
//- drawer rather than a dialog: this is a whole form plus a growing note list,
//- and a modal capped at the viewport clipped the last of it.
full-height-drawer(
    :visible="visible"
    :title="$t('Running session on {number}', { number: stash.ticketNumber })"
    size="620px"
    append-to-body
    wrapper-closable
    @update:visible="$emit('update:visible', $event)"
    @close="reset"
)
    .session-body(v-loading="loading")
        //- The clock as it stands, and why it is smaller than the wall time:
        //- a pause does not count, and the number that will be booked is the
        //- active one.
        //- The clock card IS the entry: what will be booked, since when, what
        //- for, and (on our desk) whether it bills. One grey unit above the
        //- ticket's own form, so the two are never read as one form - the
        //- ticket has a start date and notes of its own below. Saved with
        //- the ticket, without a duration, so the clock keeps running.
        .session-clock(:class="{ 'session-clock--paused': isPaused }")
            .session-clock__line
                i.session-clock__dot
                span.session-clock__active {{ activeClock }}
                span.session-clock__state {{ isPaused ? $t('paused') : $t('running') }}
                span.session-clock__pause(v-if="pausedClock") · {{ $t('Pause') }} {{ pausedClock }}
                //- Since when: moving it changes the elapsed time; never later
                //- than now (later days greyed out, a later time snaps back).
                .session-clock__since
                    span.session-clock__label {{ $t('since') }}
                    el-date-picker(
                        v-model="entry.startedAt"
                        size="mini"
                        type="datetime"
                        format="dd.MM.yyyy HH:mm"
                        :clearable="false"
                        :picker-options="startPickerOptions"
                        @change="onStartChanged"
                    )
            .session-clock__line
                el-input.session-clock__note(v-model="entry.note" size="mini" :placeholder="$t('What was done? Appears on the billing breakdown.')")
                billable-state-select(v-if="isPlatform" v-model="entry.billable" compact)
        el-form(label-position="top" @submit.native.prevent)
            el-form-item(:label="$t('Subject')")
                el-input(v-model="form.subject" :placeholder="$t('What is being worked on?')")
            el-form-item(:label="$t('Description')")
                quill-editor(v-model="form.description" :options="quillConfig")
            el-row(:gutter="12")
                el-col(:span="12")
                    el-form-item(:label="$t('Start date')")
                        el-date-picker(
                            :picker-options="{ firstDayOfWeek: 1 }"
                            v-model="form.startDate"
                            type="date"
                            format="dd.MM.yyyy"
                            value-format="yyyy-MM-dd"
                            style="width: 100%"
                            :placeholder="$t('No date')"
                        )
                el-col(:span="12")
                    el-form-item(:label="$t('Due date')")
                        el-date-picker(
                            :picker-options="{ firstDayOfWeek: 1 }"
                            v-model="form.dueDate"
                            type="date"
                            format="dd.MM.yyyy"
                            value-format="yyyy-MM-dd"
                            style="width: 100%"
                            :placeholder="$t('No date')"
                        )
            el-form-item(:label="$t('Files')")
                ticket-files-field(v-model="form.files" :ticket="ticket")

        //- A call is not one note: things get added, corrected and contradicted
        //- as it goes. So this is the running record — every note so far, each
        //- one editable, and an empty field at the end for the next one.
        .session-notes
            h4.session-notes__title {{ $t('Notes') }}
            .notes(v-loading="loadingNotes")
                p.notes__empty(v-if="!notes.length && !loadingNotes") {{ $t('No notes yet.') }}
                .note-item(v-for="item in notes" :key="item.id")
                    .note-item__head
                        span.note-item__meta {{ item.created_at | timestamp2date($t('yyyy-MM-dd') + ', HH:mm') }}
                        span.note-item__meta(v-if="item.user") · {{ item.user.first_name }} {{ item.user.last_name }}
                        el-button.note-item__edit(
                            v-if="editingId !== item.id"
                            type="text"
                            size="mini"
                            @click="startEditing(item)"
                        ) {{ $t('Edit') }}
                    p.note-item__body(v-if="editingId !== item.id" @click="startEditing(item)") {{ item.body }}
                    template(v-else)
                        el-input(v-model="editingBody" type="textarea" :rows="3")
                        .note-item__actions
                            el-button(round size="mini" icon="el-icon-fa-times fas" @click="cancelEditing") {{ $t('Cancel') }}
                            el-button(
                                round
                                size="mini"
                                type="primary"
                                icon="el-icon-fa-save fas"
                                :loading="savingEdit"
                                :disabled="!editingBody.trim()"
                                @click="saveEditing"
                            ) {{ $t('Save') }}

                .note-new
                    el-input(
                        v-model="note"
                        type="textarea"
                        :rows="3"
                        :placeholder="$t('What did the customer say?')"
                    )
                    .note-new__actions
                        el-button(
                            round
                            size="mini"
                            type="primary"
                            icon="el-icon-fa-plus fas"
                            :loading="savingNote"
                            :disabled="!note.trim()"
                            @click="saveNote"
                        ) {{ $t('Add note') }}

    //- The drawer footer as the other drawers build it: what belongs to the
    //- SESSION on the left, what belongs to this FORM on the right. Stopping the
    //- clock beside Save read like a second way to confirm, which it is not — it
    //- ends the recording.
    template(#footer-left)
        el-button(
            v-if="ticketRoute"
            round
            size="mini"
            icon="el-icon-fa-arrow-right fas"
            @click="openTicket"
        ) {{ $t('Open ticket') }}
        //- Icon only: the footer holds five buttons in 620px, and the clock
        //- line above already says "pausiert" / "läuft" in words.
        el-button(
            circle
            size="mini"
            :icon="isPaused ? 'el-icon-fa-play fas' : 'el-icon-fa-pause fas'"
            :title="isPaused ? $t('Resume') : $t('Pause')"
            :loading="pausing"
            @click="togglePause"
        )
        el-button(round size="mini" type="danger" icon="el-icon-fa-stop fas" @click="$emit('stop')")
            | {{ $t('Stop timer') }}
    template(#footer)
        el-button(round size="mini" icon="el-icon-fa-times fas" @click="$emit('update:visible', false)") {{ $t('Close') }}
        el-button(
            round
            size="mini"
            type="primary"
            icon="el-icon-fa-save fas"
            :loading="saving"
            @click="save"
        ) {{ $t('Save') }}
</template>

<i18n>
{
    "en": {
        "Running session on {number}": "Running session on {number}"
    },
    "de": {
        "since": "seit",
        "What was done? Appears on the billing breakdown.": "Was wurde gemacht? Erscheint in der Abrechnungsübersicht.",
        "paused": "pausiert",
        "running": "läuft",
        "Resume": "Fortsetzen",
        "The clock could not be paused.": "Die Erfassung konnte nicht pausiert werden.",
        "Running session on {number}": "Laufende Erfassung auf {number}",
        "Subject": "Betreff",
        "What is being worked on?": "Woran wird gearbeitet?",
        "Description": "Beschreibung",
        "Start date": "Startdatum",
        "Due date": "Fälligkeitsdatum",
        "No date": "Kein Datum",
        "Files": "Dateien",
        "Notes": "Notizen",
        "No notes yet.": "Noch keine Notizen.",
        "What did the customer say?": "Was hat die Kundin oder der Kunde gesagt?",
        "Add note": "Notiz hinzufügen",
        "Edit": "Bearbeiten",
        "Save": "Speichern",
        "Cancel": "Abbrechen",
        "Close": "Schließen",
        "Open ticket": "Ticket öffnen",
        "Stop timer": "Zeit beenden",
        "yyyy-MM-dd": "dd.MM.yyyy",
        "Ticket saved": "Ticket gespeichert",
        "Note saved": "Notiz gespeichert",
        "Something went wrong!": "Etwas ist schiefgelaufen!"
    }
}
</i18n>

<script>
/**
 * The editor behind the header pill. It edits the TICKET the clock is running
 * on, not the time entry: how long it took is the clock's business and is asked
 * for when it stops.
 *
 * Every call carries the desk's token, because our own session runs on our own
 * token even while the browser sits inside a customer's account.
 */
import _ from 'lodash';

import quillConfig from '@/quill/ticketConfig';
import { timestamp2date } from '@/filters';
import TicketApi from '@/extensions/tickets/store/api/ticket';
import FullHeightDrawer from '@/components/FullHeightDrawer';
import TicketFilesField from '@/extensions/tickets/components/TicketFilesField';
import BillableStateSelect from '@/extensions/tickets/components/BillableStateSelect';
import { useAuthStore, useTicketStore } from '@/stores';
import { activeSeconds, formatClock, pausedSecondsSoFar } from '@/helpers/duration';

export default {
    name: 'RunningSessionDrawer',
    components: {
        'full-height-drawer': FullHeightDrawer,
        'billable-state-select': BillableStateSelect,
        TicketFilesField,
    },
    filters: { timestamp2date },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        // The running session this panel belongs to, as the header pill knows
        // it: which ticket, on which desk.
        stash: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            quillConfig,
            loading: false,
            saving: false,
            pausing: false,
            now: Date.now(),
            ticker: null,
            ticket: null,
            form: {
                subject: '',
                description: '',
                startDate: null,
                dueDate: null,
                files: [],
            },
            // The running entry's own fields (see the template).
            entry: {
                startedAt: null,
                note: '',
                billable: true,
            },
            notes: [],
            loadingNotes: false,
            note: '',
            savingNote: false,
            editingId: null,
            editingBody: '',
            savingEdit: false,
        };
    },
    computed: {
        isPlatform() {
            return _.get(this.stash, 'desk') === 'platform';
        },
        // Days after today are greyed out; a time past now snaps back to now
        // (onStartChanged), and the server refuses it too.
        startPickerOptions() {
            return { firstDayOfWeek: 1, disabledDate: (date) => date.getTime() > Date.now() };
        },
        isPaused() {
            return !!this.stash.pausedAt;
        },
        // The clock counts from the start in the picker, not from the saved
        // one: moving the start shows what will be booked before Save, on
        // the number itself rather than in a sentence beside it.
        activeClock() {
            const startedAt = this.entry.startedAt ? Math.floor(this.entry.startedAt.getTime() / 1000) : this.stash.startedAt;

            return formatClock(activeSeconds({ ...this.stash, startedAt }, this.now));
        },
        pausedClock() {
            const seconds = pausedSecondsSoFar(this.stash, this.now);

            return seconds ? formatClock(seconds) : '';
        },
        ticketStore() {
            return useTicketStore();
        },
        // Our own desk lives under /system, a shop's own under the shop. During
        // a mimic our ticket is not reachable at all from inside the account
        // (the token is the customer's), so the link is simply not offered
        // then. Decided on the TOKEN, not on the page: a super user on their
        // own dashboard can open /system/tickets as much as from /system.
        ticketRoute() {
            const id = _.get(this.stash, 'ticketId');
            if (!id) return null;

            if (_.get(this.stash, 'desk') === 'platform') {
                const auth = useAuthStore();

                return auth.isSuperUser && !auth.isImpersonating ? `/system/tickets/${id}` : null;
            }

            return `/tickets/${id}`;
        },
    },
    watch: {
        visible(visible) {
            if (visible) this.load();
        },
        // The two ends of one span. Picking a date that would turn it inside
        // out pulls the OTHER end along instead of being refused — the ticket
        // form's own behaviour, and the server refuses the inverted span.
        'form.startDate'(value) {
            if (value && this.form.dueDate && value > this.form.dueDate) this.form.dueDate = value;
        },
        'form.dueDate'(value) {
            if (value && this.form.startDate && value < this.form.startDate) this.form.startDate = value;
        },
    },
    mounted() {
        this.ticker = setInterval(() => {
            this.now = Date.now();
        }, 1000);
    },
    beforeDestroy() {
        if (this.ticker) clearInterval(this.ticker);
    },
    methods: {
        togglePause() {
            if (this.pausing) return;

            this.pausing = true;
            this.ticketStore
                .togglePause(this.stash.desk)
                .catch((error) => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(error);
                    this.notify(_.get(error, 'message') || this.$t('The clock could not be paused.'), 'error');
                })
                .finally(() => {
                    this.pausing = false;
                });
        },
        load() {
            const id = _.get(this.stash, 'ticketId');
            if (!id) return;

            this.loading = true;
            TicketApi.fetch(id)
                .then((ticket) => {
                    this.ticket = ticket;
                    this.form = {
                        subject: ticket.subject || '',
                        description: ticket.description || '',
                        startDate: ticket.start_date || null,
                        dueDate: ticket.due_date || null,
                        files: ticket.files || [],
                    };
                    // The running entry is one of the ticket's; the stash
                    // knows which.
                    const running = (ticket.timeEntries || []).find((item) => parseInt(item.id, 10) === parseInt(this.stash.entryId, 10));
                    this.entry = {
                        startedAt: new Date((running ? running.started_at : this.stash.startedAt) * 1000),
                        note: (running && running.note) || '',
                        billable: running ? running.billable : true,
                    };
                })
                .catch((error) => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(error);
                    this.notify(this.$t('Something went wrong!'), 'error');
                })
                .finally(() => {
                    this.loading = false;
                });

            this.loadNotes();
        },
        reset() {
            this.ticket = null;
            this.notes = [];
            this.note = '';
            this.cancelEditing();
        },
        onStartChanged(start) {
            if (start && start.getTime() > Date.now()) this.entry.startedAt = new Date();
        },
        save() {
            const id = _.get(this.stash, 'ticketId');
            if (!id) return;

            this.saving = true;
            Promise.all([
                TicketApi.update(
                    id,
                    {
                        subject: String(this.form.subject || '').trim() || null,
                        description: this.form.description || null,
                        start_date: this.form.startDate || null,
                        due_date: this.form.dueDate || null,
                        // upload:true = a fresh upload the backend still has to
                        // convert into a File; upload:false = a library file.
                        files: this.form.files.map((file) => ({ id: file.id, upload: file.upload !== false })),
                    }
                ),
                // No duration: a duration is what ends an entry.
                TicketApi.saveTimeEntry({
                    id: this.stash.entryId,
                    entry: {
                        started_at: this.toDateTimeString(this.entry.startedAt),
                        note: this.entry.note.trim() || undefined,
                        billable: this.entry.billable,
                    },
                }),
            ])
                .then(([ticket, entry]) => {
                    // The files as the server now holds them: a fresh upload
                    // came back as a File with a new id, and the upload it was
                    // made from is gone. Kept as sent, the next save would
                    // offer that upload again and lose the file.
                    this.form.files = ticket.files || [];
                    // The pill labels itself with the subject and counts from
                    // the start, so both have to reach the stash it reads.
                    this.ticketStore.writeTrackingStash(this.stash.desk, {
                        ...this.stash,
                        subject: ticket.subject,
                        startedAt: entry.started_at,
                    });
                    this.ticketStore.threadChanged();
                    this.ticketStore.sessionChanged();
                    this.notify(this.$t('Ticket saved'));
                })
                .catch((error) => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(error);
                    this.notify(_.get(error, 'message') || this.$t('Something went wrong!'), 'error');
                })
                .finally(() => {
                    this.saving = false;
                });
        },
        toDateTimeString(date) {
            const pad = (value) => String(value).padStart(2, '0');

            return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
        },
        openTicket() {
            const route = this.ticketRoute;
            if (!route) return;

            this.$emit('update:visible', false);
            if (this.$route.path !== route) this.$router.push(route);
        },
        loadNotes() {
            this.loadingNotes = true;

            TicketApi.fetchNotes(this.stash.ticketId)
                .then((notes) => {
                    this.notes = notes || [];
                })
                .catch(() => {
                    this.notes = [];
                })
                .finally(() => {
                    this.loadingNotes = false;
                });
        },
        startEditing(item) {
            this.editingId = item.id;
            this.editingBody = item.body;
        },
        cancelEditing() {
            this.editingId = null;
            this.editingBody = '';
        },
        saveEditing() {
            this.savingEdit = true;

            TicketApi.updateNote(this.editingId, this.editingBody.trim())
                .then((updated) => {
                    const item = this.notes.find((note) => note.id === this.editingId);
                    if (item) this.$set(item, 'body', updated.body);
                    this.cancelEditing();
                    this.ticketStore.threadChanged();
                })
                .catch((error) => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(error);
                    this.notify(this.$t('Something went wrong!'), 'error');
                })
                .finally(() => {
                    this.savingEdit = false;
                });
        },
        saveNote() {
            this.savingNote = true;

            // An internal note on the ticket's own thread — the same note the
            // ticket detail writes, so it is there when the time is applied.
            TicketApi.createNote(this.stash.ticketId, this.note.trim())
                .then((created) => {
                    this.note = '';
                    // Stay open: a call produces several notes, not one.
                    this.notes.push(created);
                    this.ticketStore.threadChanged();
                    this.notify(this.$t('Note saved'));
                })
                .catch((error) => {
                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(error);
                    this.notify(this.$t('Something went wrong!'), 'error');
                })
                .finally(() => {
                    this.savingNote = false;
                });
        },
        notify(message, type = 'success') {
            this.$notify[type]({
                title: message,
                iconClass: type === 'error' ? 'fas el-icon-fa-xmark' : 'fas el-icon-fa-check',
                position: 'bottom-left',
                customClass: 'dark',
                duration: 8000,
            });
        },
    },
};
</script>

<style lang="sass" scoped>
.session-notes
    margin-top: 6px
    padding-top: 14px
    border-top: 1px solid #ebeef5

.session-notes__title
    margin: 0 0 6px
    font-size: .82rem
    font-weight: 600
    color: #606266

.notes__empty
    color: #909399
    font-size: .82rem
    margin: 0 0 12px

.note-item
    padding: 10px 0
    border-bottom: 1px solid #ebeef5

    &__head
        display: flex
        align-items: center
        gap: 6px
        margin-bottom: 4px

    &__meta
        font-size: .72rem
        color: #909399

    &__edit
        margin-left: auto
        padding: 0

    &__body
        margin: 0
        font-size: .86rem
        line-height: 1.45
        white-space: pre-wrap
        word-break: break-word
        cursor: text

    &__actions
        display: flex
        justify-content: flex-end
        gap: 8px
        margin-top: 8px
        // The framework's own button spacing would stack on top of the gap.
        .el-button + .el-button
            margin-left: 0

.note-new
    padding-top: 14px

    &__actions
        display: flex
        justify-content: flex-end
        margin-top: 8px

// The clock card, above the form: what will be booked, since when, what for,
// whether it bills. Two lines in one grey unit; the ticket's own fields start
// under it with labels of their own, so nothing here carries a label.
.session-clock
    margin-bottom: 18px
    padding: 10px 12px
    border: 1px solid #ebeef5
    border-radius: 6px
    background: #f5f7fa
    font-variant-numeric: tabular-nums

    &__line
        display: flex
        align-items: center
        gap: 8px

        & + &
            margin-top: 8px

    // "seit" and its picker sit at the line's end, the clock at its start.
    &__since
        display: flex
        align-items: center
        gap: 6px
        margin-left: auto

        ::v-deep .el-date-editor
            width: 170px

    &__label
        color: #909399

    &__note
        flex: 1
        min-width: 0

    &__dot
        width: 8px
        height: 8px
        border-radius: 50%
        background: #67c23a

    // One size for the whole line; the number is only bolder, not bigger.
    &__active
        font-weight: 600
        color: #303133

    &__state,
    &__pause
        color: #909399

    &--paused
        .session-clock__dot
            background: #E6A23C

        .session-clock__active
            color: #606266
</style>
